import React from 'react';
import { AgencyReportRow } from '@/store/agencyReports';

import AppTable from '@/components/AppTable';
import AppTableContainer from '@/components/AppTableContainer';
import AppTableHead from '@/components/AppTable/AppTableHead';
import AppTableRow from '@/components/AppTable/AppTableRow';
import AppTableBody from '@/components/AppTable/AppTableBody';
import AppTableCell from '@/components/AppTable/AppTableCell';
import AppTableHeaderCell from '@/components/AppTable/AppTableHeaderCell';
import AppBox from '@/components/AppBox';
import AppIcon from '@/components/AppIcon';
import AppText from '@/components/AppText';
import AppTooltip from '@/components/AppTooltip';
import AppLoader from '@/components/AppLoader';
import { tooltipTitles } from '@/constants/tooltipTitles';

interface Props {
  tableRows: AgencyReportRow[];
  onDownload: (reportId: number | null) => void;
  loadingReports: { [key: number]: boolean };
}

const AgencyUsersReportsTable: React.FC<Props> = ({ tableRows, onDownload, loadingReports }) => {
  return (
    <AppTableContainer>
      <AppTable>
        <AppTableHead>
          <AppTableRow>
            <AppTableHeaderCell>User</AppTableHeaderCell>
            <AppTableHeaderCell>Company</AppTableHeaderCell>

            <AppTableHeaderCell>
              <AppTooltip title={tooltipTitles.total_leads}>
                <AppBox className="flex items-center gap-1">
                  <AppText>Total Leads</AppText>
                  <AppIcon name="help" size="sm" className="text-gray-400" />
                </AppBox>
              </AppTooltip>
            </AppTableHeaderCell>

            <AppTableHeaderCell>
              <AppTooltip title={tooltipTitles.message_leads}>
                <AppBox className="flex items-center gap-1">
                  <AppText>Message Leads</AppText>
                  <AppIcon name="help" size="sm" className="text-gray-400" />
                </AppBox>
              </AppTooltip>
            </AppTableHeaderCell>

            <AppTableHeaderCell>
              <AppTooltip title={tooltipTitles.message_leads_responded}>
                <AppBox className="flex items-center gap-1">
                  <AppText>Message Leads Responded</AppText>
                  <AppIcon name="help" size="sm" className="text-gray-400" />
                </AppBox>
              </AppTooltip>
            </AppTableHeaderCell>

            <AppTableHeaderCell>
              <AppTooltip title={tooltipTitles.missed_call_leads}>
                <AppBox className="flex items-center gap-1">
                  <AppText>Missed Call Leads</AppText>
                  <AppIcon name="help" size="sm" className="text-gray-400" />
                </AppBox>
              </AppTooltip>
            </AppTableHeaderCell>

            <AppTableHeaderCell>
              <AppTooltip title={tooltipTitles.answered_call_leads}>
                <AppBox className="flex items-center gap-1">
                  <AppText>Answered Call Leads</AppText>
                  <AppIcon name="help" size="sm" className="text-gray-400" />
                </AppBox>
              </AppTooltip>
            </AppTableHeaderCell>

            <AppTableHeaderCell className="text-center">Download</AppTableHeaderCell>
          </AppTableRow>
        </AppTableHead>

        <AppTableBody>
          {tableRows.length ? (
            tableRows.map((row) => (
              <AppTableRow key={row.user_id}>
                <AppTableCell><AppText>{row.name}</AppText></AppTableCell>
                <AppTableCell><AppText>{row.company ?? '-'}</AppText></AppTableCell>
                <AppTableCell><AppText>{row.total}</AppText></AppTableCell>
                <AppTableCell><AppText>{row.message}</AppText></AppTableCell>
                <AppTableCell>
                  <AppText>
                    {row.message > 0
                      ? `${row.message_responded} (${((row.message_responded / row.message) * 100).toFixed(1)}%)`
                      : '0 (0%)'}
                  </AppText>
                </AppTableCell>
                <AppTableCell><AppText>{row.missed_call}</AppText></AppTableCell>
                <AppTableCell><AppText>{row.answered_call}</AppText></AppTableCell>
                <AppTableCell>
                  <AppBox center>
                    {loadingReports[row.id || 0] ? (
                      <AppLoader size={20} color="primary" />
                    ) : (
                      row.id && (
                        <AppTooltip title={tooltipTitles.download_user_report}>
                          <AppIcon
                            name="download"
                            className="text-primary cursor-pointer hover:text-primary-hover"
                            onClick={() => onDownload(row.id)}
                          />
                        </AppTooltip>
                      )
                    )}
                  </AppBox>
                </AppTableCell>
              </AppTableRow>
            ))
          ) : (
            <AppTableRow>
              <AppTableCell colSpan={8} className="text-center">
                No results
              </AppTableCell>
            </AppTableRow>
          )}
        </AppTableBody>
      </AppTable>
    </AppTableContainer>
  );
};

export default AgencyUsersReportsTable; 